import {Fragment, ReactElement, useContext, useState} from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Typography, Paper, Collapse, useMediaQuery, debounce,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {DateTime} from "luxon";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import {
  IDictionary,
  IFilter,
  IFilterValues,
  ISelectedNode,
  IState,
} from "../types";
import {SystemContext} from "../context/systemContext";
import {getDefaultLocaleConfig} from "../configuration/configuration";
import {
  getCurrentDayRange,
  getCurrentMonthRange,
  getCurrentWeekRange, getLast30DaysRange,
  getLast7DaysRange,
  getLastMonthRange,
  getLastWeekRange, logger
} from "../system/utils";
import {useDispatch, useSelector} from "react-redux";
import {updateFilter} from "../actions";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterLuxon} from "@mui/x-date-pickers/AdapterLuxon";
import Select, {SelectChangeEvent} from "@mui/material/Select";

interface IFilterComponentBaseProps {
  customFilter?: IDictionary<string>;
  children?: ReactElement | ReactElement[] | undefined;
  loading: boolean;
  toolbar?: ReactElement | ReactElement[];
  disabled?: Record<string, true>;
  onOpen?: () => void;
  onClose?: (accepted: boolean) => void;
}

export interface IFilterRTValues {
  mode: string;
  crew: string;
  shift: string;
  selectedFrom: DateTime;
  selectedTo: DateTime;
}

const convertFromFilterValues = (filter: IFilterValues): IFilterRTValues => {
  return {
    mode: filter.mode,
    crew: filter.crew,
    shift: filter.shift,
    selectedFrom: DateTime.fromISO(filter.selectedFrom),
    selectedTo: DateTime.fromISO(filter.selectedTo)
  };
};

const convertFromRTValues = (filter: IFilterRTValues): IFilterValues => {
  return {
    mode: filter.mode,
    crew: filter.crew,
    shift: filter.shift,
    selectedFrom: filter.selectedFrom.toISODate()!,
    selectedTo: filter.selectedTo.toISODate()!
  };
}

const FilterComponentBase = ({
                               children,
                               customFilter,
                               loading,
                               toolbar,
                               disabled,
                               onOpen, onClose
                             }: IFilterComponentBaseProps): ReactElement => {
  const dispatcher = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const filterData = useSelector((state: IState) => state.filter);
  const [filterValues, setFilterValues] = useState<IFilterRTValues>(convertFromFilterValues(filterData.local));
  const [open, setOpen] = useState<boolean>(false);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const matchSM = useMediaQuery("(min-width:1500px)");

  const setDateFilter = (dateFilter: string) => {
    let selectedFrom = filterValues.selectedFrom;
    let selectedTo = filterValues.selectedTo;

    if (dateFilter === "CURRENT_WEEK" || dateFilter === "RANGE") {
      [selectedFrom, selectedTo] = getCurrentWeekRange();
    } else if (dateFilter === "LAST_WEEK") {
      [selectedFrom, selectedTo] = getLastWeekRange();
    } else if (dateFilter === "LAST_7_DAYS") {
      [selectedFrom, selectedTo] = getLast7DaysRange();
    } else if (dateFilter === "CURRENT_MONTH") {
      [selectedFrom, selectedTo] = getCurrentMonthRange();
    } else if (dateFilter === "LAST_MONTH") {
      [selectedFrom, selectedTo] = getLastMonthRange();
    } else if (dateFilter === "LAST_30_DAYS") {
      [selectedFrom, selectedTo] = getLast30DaysRange();
    } else if (dateFilter === "FACTORY_DAY") {
      [selectedFrom, selectedTo] = getCurrentDayRange();
    }

    setFilterValues({...filterValues, selectedTo, selectedFrom, mode: dateFilter});
  };

  const handleDateChange = (date: any) => {
    if (date && date.isValid) {
      setFilterValues({...filterValues, selectedTo: date});
    }
  };

  const handleDateChangeFrom = (date: any) => {
    if (date && date.isValid) {
      setFilterValues({...filterValues, selectedFrom: date});
    }
  };

  const handleDateChangeTo = (date: any) => {
    if (date && date.isValid) {
      setFilterValues({...filterValues, selectedTo: date});
    }
  };

  const onAccept = (newDate: DateTime | null = null, type: "FACTORY_DATE" | "FROM" | "TO" = "FACTORY_DATE") => {
    logger("FilterComponentBase.onAccept");
    debounce(() => {
      logger("FilterComponentBase.onAccept.debounce");
      let newFilter: IFilter = {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type,
        crew: filterValues.crew,
        shift: filterValues.shift
      };

      switch (filterValues.mode) {
        case "FACTORY_DAY":
          newFilter = {
            ...newFilter,
            factoryDay: (newDate ?? filterValues.selectedTo).toISODate()!
          };
          break;

        case "REAL_TIME":
          break;

        default:
          newFilter = {
            ...newFilter,
            fromDate: (type === "FROM" ? newDate ?? filterValues.selectedFrom : filterValues.selectedFrom).toISODate()!,
            toDate: (type === "TO" ? newDate ?? filterValues.selectedTo : filterValues.selectedTo).toISODate()!
          };
          break;
      }

      dispatcher(updateFilter({local: {...convertFromRTValues(filterValues)}, remote: newFilter}));
      setOpen(false);

      if (onClose) {
        onClose(true);
      }
    }, 500)();
  };

  const onCancel = () => {
    setFilterValues(convertFromFilterValues(filterData.local));
    setOpen(false);
    if (onClose) {
      onClose(false);
    }

  };

  const forward = (type: "FACTORY_DATE" | "FROM" | "TO" = "FACTORY_DATE", editMode = false) => {
    if (type === "FROM") {
      const datetime = filterValues.selectedFrom.plus({days: 1});
      setFilterValues({...filterValues, selectedFrom: datetime});
      if (!editMode) {
        onAccept(datetime, type);
      }
    } else {
      const datetime = filterValues.selectedTo.plus({days: 1});
      setFilterValues({...filterValues, selectedTo: datetime});
      if (!editMode) {
        onAccept(datetime, type);
      }
    }
  };

  const backward = (type: "FACTORY_DATE" | "FROM" | "TO" = "FACTORY_DATE", editMode = false) => {
    if (type === "FROM") {
      const datetime = filterValues.selectedFrom.plus({days: -1});
      setFilterValues({...filterValues, selectedFrom: datetime});
      if (!editMode) {
        onAccept(datetime, type);
      }
    } else {
      const datetime = filterValues.selectedTo.plus({days: -1});
      setFilterValues({...filterValues, selectedTo: datetime});
      if (!editMode) {
        onAccept(datetime, type);
      }
    }
  };

  const shiftObj = systemContext.shifts.find(x => x.code === filterValues.shift);
  const shiftName = shiftObj ? shiftObj.name : filterValues.shift;
  const crewObj = systemContext.crews.find(x => x.code === filterValues.crew);
  const crewName = crewObj ? crewObj.name : filterValues.crew;

  // const toggleOpen = () => setOpen(!open);

  // const onEnterKey = (event: KeyboardEvent) => {
  //   if (event.key === "Enter") {
  //     onAccept();
  //   }
  // };

  const optionValues = [{value: "CURRENT_WEEK", text: systemContext.language.currentWeek},
    {value: "LAST_WEEK", text: systemContext.language.lastWeek},
    {value: "LAST_7_DAYS", text: systemContext.language.last7Days},
    {value: "CURRENT_MONTH", text: systemContext.language.currentMonth},
    {value: "LAST_MONTH", text: systemContext.language.lastMonth},
    {value: "LAST_30_DAYS", text: systemContext.language.last30Days},
    {value: "FACTORY_DAY", text: systemContext.language.factoryDay},
    {value: "RANGE", text: systemContext.language.range},
    {value: "REAL_TIME", text: systemContext.language.realTime},
  ];

  const onExpandClick = () => {
    setOpen(!open);
    if (!open && onOpen) {
      onOpen();
    }

    if (open && onClose) {
      onClose(false);
    }

  }

  const selectedText = optionValues.find(x => x.value === filterValues.mode);

  const ableToChangeDate = filterValues.mode === "RANGE" || filterValues.mode === "FACTORY_DAY";

  return (
      <Paper className="p-1 mb-1 w-100">
        <div className="display-flex-row gap-1 p-1 mb align-items-center" style={{padding: ".25em .5em"}}>
          <IconButton onClick={onExpandClick} size={"small"} className="mr-1">
            {open
                ? <ExpandLessIcon fontSize="inherit" fontWeight="bold"/>
                : <ExpandMoreIcon fontSize="inherit" fontWeight="bold"/>
            }
          </IconButton>

          {open && <Typography onClick={onExpandClick}>{systemContext.language.filter}</Typography>}

          {!open && filterValues.mode === "REAL_TIME" &&
              <strong className="flex-grow-0 mr-1" onClick={onExpandClick}>{systemContext.language.realTime}</strong>}


          {!open && filterValues.mode === "FACTORY_DAY" &&
              <>
                {filterValues.mode === "FACTORY_DAY" &&
                    <strong className="flex-grow-0" onClick={onExpandClick}>{systemContext.language.factoryDay}:</strong>}

                <IconButton color="primary" onClick={() => backward()} size={"small"} disabled={loading}>
                  <KeyboardArrowLeftIcon fontSize="inherit" fontWeight="bold"/>
                </IconButton>

                <u><strong
                    onClick={onExpandClick}>{filterValues.selectedTo.toFormat(getDefaultLocaleConfig().dateFormat)}</strong></u>

                <IconButton color="primary" onClick={() => forward()} size={"small"} disabled={loading}>
                  <KeyboardArrowRight fontSize="inherit" fontWeight="bold"/>
                </IconButton>

              </>
          }

          {!open && filterValues.mode !== "FACTORY_DAY" && filterValues.mode !== "REAL_TIME" &&
              <>
                <strong className="flex-grow-0 mr-1" onClick={onExpandClick}>{selectedText?.text}</strong>

                <strong className="flex-grow-0" onClick={onExpandClick}>{systemContext.language.from}:</strong>

                {!ableToChangeDate && <IconButton color="primary" onClick={() => backward("FROM")} size={"small"}>
                  <KeyboardArrowLeftIcon fontSize="inherit" fontWeight="bold"/>
                </IconButton>}

                <u><strong
                    onClick={onExpandClick}>{filterValues.selectedFrom.toFormat(getDefaultLocaleConfig().dateFormat)}</strong></u>

                {!ableToChangeDate && <IconButton color="primary" onClick={() => forward("FROM")} size={"small"}>
                  <KeyboardArrowRight fontSize="inherit" fontWeight="bold"/>
                </IconButton>}

                <strong className="flex-grow-0 ml-2"
                        onClick={onExpandClick}>{systemContext.language.to}:</strong>

                {!ableToChangeDate && <IconButton color="primary" onClick={() => backward("TO")} size={"small"}>
                  <KeyboardArrowLeftIcon fontSize="inherit" fontWeight="bold"/>
                </IconButton>}

                <u><strong
                    onClick={onExpandClick}>{filterValues.selectedTo.toFormat(getDefaultLocaleConfig().dateFormat)}</strong></u>

                {!ableToChangeDate && <IconButton color="primary" onClick={() => forward("TO")} size={"small"}>
                  <KeyboardArrowRight fontSize="inherit" fontWeight="bold"/>
                </IconButton>}
              </>
          }

          {!open && matchSM &&
              <>
                {(!disabled || !disabled.shift) &&
                    <>
                      <strong className="flex-grow-0 ml-1 mr-1"
                              onClick={onExpandClick}>{systemContext.language.shift}:</strong>
                      <u><strong>{shiftName}</strong></u>
                    </>
                }

                {(!disabled || !disabled.shift) &&
                    <>
                      <strong className="flex-grow-0 ml-2 mr-1"
                              onClick={onExpandClick}>{systemContext.language.crew}:</strong>
                      <u><strong>{crewName}</strong></u>
                    </>
                }

                {customFilter && Object.keys(customFilter).map((field, index) => {

                  return (
                      <Fragment key={`custom-${field}-${index}`}>
                        <strong className="flex-grow-0 ml-2 mr-1" onClick={onExpandClick}>{field}:</strong>
                        <strong>{customFilter[field]}</strong>
                      </Fragment>
                  );
                })
                }
              </>
          }

          <div className="flex-grow-1"/>

          {toolbar}

        </div>
        <Collapse in={open} timeout="auto" unmountOnExit={true}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <div className="display-flex-row gap-2 mb-1">
              <FormControl className="form-control">
                <InputLabel id="date-mode-label">Date Mode</InputLabel>
                <Select
                    variant="standard"
                    labelId="date-mode-label"
                    id="date-mode-select"
                    value={filterValues.mode}
                    displayEmpty={true}
                    onChange={(event: SelectChangeEvent) => setDateFilter(event.target.value as string)}>
                  {optionValues.map((item, index) =>
                      <MenuItem key={`filter-option-${index}`} value={item.value}>{item.text}</MenuItem>
                  )
                  }
                </Select>
              </FormControl>


              {filterValues.mode === "FACTORY_DAY" &&
                  <div className="display-flex-row flex-grow-0 gap-1" style={{display: "inline-flex"}}>
                    <IconButton color="primary" onClick={() => backward("FACTORY_DATE", true)} size={"small"}
                                style={{marginTop: "1em"}}>
                      <KeyboardArrowLeftIcon fontSize="inherit" fontWeight="bold"/>
                    </IconButton>
                    <DatePicker
                        label={systemContext.language.factoryDay}
                        value={filterValues.selectedTo}
                        maxDate={DateTime.now()}
                        slotProps={{textField: {variant: "standard"}}}
                        onChange={handleDateChange}/>
                    <IconButton color="primary" onClick={() => forward("FACTORY_DATE", true)} size={"small"}
                                style={{marginTop: "1em"}}>
                      <KeyboardArrowRight fontSize="inherit" fontWeight="bold"/>
                    </IconButton>
                  </div>
              }

              {filterValues.mode === "RANGE" &&
                  <>
                    <DatePicker
                        label={systemContext.language.from}
                        value={filterValues.selectedFrom}
                        maxDate={DateTime.now()}
                        slotProps={{textField: {variant: "standard"}}}
                        onChange={handleDateChangeFrom}/>

                    <DatePicker
                        label={systemContext.language.to}
                        value={filterValues.selectedTo}
                        maxDate={DateTime.now()}
                        slotProps={{textField: {variant: "standard"}}}
                        onChange={handleDateChangeTo}/>
                  </>
              }


              {(!disabled || !disabled.shift) &&
                  <FormControl className="form-control">
                    <InputLabel id="shift-label">{systemContext.language.shift}</InputLabel>
                    <Select
                        variant="standard"
                        labelId="shift-label"
                        id="shift-select"
                        value={filterValues.shift}
                        displayEmpty={true}
                        onChange={(event: SelectChangeEvent) => setFilterValues({
                          ...filterValues,
                          shift: event.target.value as string
                        })}>
                      {systemContext.shifts.map((shiftItem) => {
                        return <MenuItem key={`crew-${shiftItem.shiftID}`}
                                         value={shiftItem.code}>{shiftItem.name}</MenuItem>;
                      })
                      }
                      <MenuItem value={systemContext.language.all.toUpperCase()}>{systemContext.language.all}</MenuItem>
                    </Select>
                  </FormControl>
              }
              {(!disabled || !disabled.crew) &&
                  <FormControl className="form-control">
                    <InputLabel id="crew-label">{systemContext.language.crew}</InputLabel>
                    <Select
                        variant="standard"
                        labelId="crew-label"
                        id="crew-select"
                        value={filterValues.crew}
                        displayEmpty={true}
                        onChange={(event: SelectChangeEvent) => setFilterValues({
                          ...filterValues,
                          crew: event.target.value as string
                        })}>
                      {systemContext.crews.map((crewItem) => {
                        return <MenuItem key={`crew-${crewItem.crewID}`}
                                         value={crewItem.code}>{crewItem.name}</MenuItem>;
                      })
                      }
                      <MenuItem value={systemContext.language.all.toUpperCase()}>{systemContext.language.all}</MenuItem>
                    </Select>
                  </FormControl>
              }

              {children}

              <IconButton color="primary" sx={{verticalAlign: "bottom" }}
                          onClick={() => onAccept()}>
                <CheckIcon/>
              </IconButton>

              <IconButton color="secondary" sx={{verticalAlign: "bottom"}} onClick={onCancel}>
                <ClearIcon/>
              </IconButton>
            </div>
          </LocalizationProvider>

        </Collapse>
      </Paper>
  );
};

export default FilterComponentBase;
